import { useCallback, useEffect, useRef, useState } from "react";

/** Matches backend `OTP_EXPIRY_SECONDS` (5 minutes). */
export const OTP_EXPIRY_SECONDS = 300;

/** Formats seconds as `M:SS` (e.g. 5:00 … 0:01). */
export function formatOtpCountdown(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

export function resolveOtpExpirySeconds(
  expiresInSeconds?: number | null,
): number {
  if (
    typeof expiresInSeconds === "number" &&
    Number.isFinite(expiresInSeconds) &&
    expiresInSeconds > 0
  ) {
    return Math.floor(expiresInSeconds);
  }
  return OTP_EXPIRY_SECONDS;
}

export function useOtpExpiryCountdown() {
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startCountdown = useCallback(
    (seconds: number = OTP_EXPIRY_SECONDS) => {
      clearTimer();
      const initial = Math.max(1, Math.floor(seconds));
      setRemainingSeconds(initial);
      timerRef.current = window.setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            clearTimer();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [clearTimer],
  );

  const resetCountdown = useCallback(() => {
    clearTimer();
    setRemainingSeconds(0);
  }, [clearTimer]);

  useEffect(() => () => clearTimer(), [clearTimer]);

  return {
    remainingSeconds,
    formattedCountdown: formatOtpCountdown(remainingSeconds),
    isActive: remainingSeconds > 0,
    startCountdown,
    resetCountdown,
  };
}
